import axios, { AxiosResponse } from 'axios';
import { has, includes, isEmpty, isFunction, map } from 'lodash';

import { cancelGetS3Meta, cancelSaveAsset, getS3Meta, saveAsset, S3MetaArgs, Asset } from 'common/assets/Assets';

const { CancelToken } = axios;

interface UploadProgressFunc {
    /** progress percentage */
    (progress: number): void;
}

interface UploadFileAssetArgs extends S3MetaArgs {
    shortenExternalUrl?: boolean;
}

interface UploadFileAssetParams {
    agencyId: number;
    tenantId: number;
    file: File;
    args: UploadFileAssetArgs;
    handleUploadProgress?: UploadProgressFunc;
}

export enum MediaType {
    Image = 'image',
    Video = 'video',
    Audio = 'audio',
    Text = 'text',
    Application = 'application',
    TextPlain = 'txt',
    Pdf = 'pdf',
    Doc = 'doc',
    Docx = 'docx',
    Xls = 'xls',
    Xlsx = 'xlsx',
    Csv = 'csv',
    Mov = 'mov',
    Mp4 = 'mp4',
    Jpeg = 'jpeg',
    Jpg = 'jpg',
    Png = 'png',
    Msg = 'msg',
    Mp3 = 'mp3',
    Raw = 'raw',
}

export interface UploadedFileAsset {
    id: string;
    lastModified?: number;
    name: string;
    size: number;
    type: string;
    url: string;
    mediaFormat?: MediaType;
    isImage?: boolean;
    isVideo?: boolean;
}

let cancelCloudUploadSource = CancelToken.source();

export const isUploadCancelError = (error: any): boolean => {
    return axios.isCancel(error);
};

const getFileUrl = (uploadResponse: any): string => {
    let fileUrl = '';

    if (has(uploadResponse, 'data')) {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(uploadResponse.data, 'text/html');
        const locations = htmlDoc.getElementsByTagName('Location');

        if (locations && locations.length) {
            fileUrl = decodeURIComponent(locations[0].innerHTML);
        }
    }
    return fileUrl;
};

const aggregateUploadProgress = (handleProgress: any, currentProgress: number, maxProgress: number): any => {
    return (progress: number): void => {
        if (handleProgress) {
            const newProgress = (progress * maxProgress) / 100 + currentProgress;

            handleProgress(newProgress);
        }
    };
};

export const uploadFileAsset = async ({
    agencyId,
    tenantId,
    file,
    args,
    handleUploadProgress,
}: UploadFileAssetParams): Promise<UploadedFileAsset> => {
    const contentType = isEmpty(file.type) ? 'application/octet-stream' : file.type;
    const fileName = file.name.replace(/\s|\+|#/g, '_');
    const fileNameValues = fileName.split('.');
    const newArgs: any = { ...args };

    newArgs.fileName = map(fileNameValues, (fileValue, fileIndex) => {
        return fileIndex === fileNameValues.length - 1 ? `_${Date.now()}.${fileValue}` : fileValue;
    }).join('_');

    const s3MetaData = await getS3Meta({
        agencyId,
        tenantId,
        options: {
            contentType,
            version: 'v4',
            args: newArgs,
        },
        handleUploadProgress: aggregateUploadProgress(handleUploadProgress, 0, 30),
    });
    const bodyFormData = new FormData();

    bodyFormData.append('key', s3MetaData.key);
    bodyFormData.append('acl', s3MetaData.acl);
    bodyFormData.append('success_action_status', s3MetaData.successActionStatus);
    bodyFormData.append('Content-Type', contentType);
    bodyFormData.append('x-amz-credential', s3MetaData.credentials);
    bodyFormData.append('x-amz-algorithm', s3MetaData.algorithm);
    bodyFormData.append('x-amz-date', s3MetaData.date);
    bodyFormData.append('policy', s3MetaData.policy);
    bodyFormData.append('x-amz-signature', s3MetaData.signature);
    bodyFormData.append('file', file);
    const uploadResponse: AxiosResponse = await axios({
        method: 'post',
        url: s3MetaData.bucketUrl,
        data: bodyFormData,
        cancelToken: cancelCloudUploadSource.token,
        onUploadProgress: (event: any) => {
            const progressCb = aggregateUploadProgress(handleUploadProgress, 30, 50);

            if (progressCb) {
                const progress = event.total ? (event.loaded / event.total) * 100 : 100;

                progressCb(progress);
            }
        },
    });

    if (uploadResponse.status !== 201) {
        throw Error(uploadResponse.data);
    }
    const url = getFileUrl(uploadResponse);
    const splitKey = s3MetaData.key.split('/');
    const asset: Asset = {
        assetKey: splitKey[splitKey.length - 2],
        objectID: s3MetaData.key,
        fileName,
        extension: fileName.split('.').pop()!,
        contentLength: file.size,
        internalURL: url,
        accessPolicy: args.accessPolicy,
    };

    if (args.moduleId) {
        asset.moduleID = args.moduleId;
    }
    if (args.expireDate) {
        asset.expireDate = args.expireDate;
    }
    const saveAssetResponse: Asset = await saveAsset({
        agencyId,
        tenantId,
        asset,
        args: {
            shortenExternalUrl: args.shortenExternalUrl,
        },
        handleUploadProgress: aggregateUploadProgress(handleUploadProgress, 80, 20),
    });
    const uploadFileAssetResponse: UploadedFileAsset = {
        id: saveAssetResponse.id!,
        lastModified: file.lastModified,
        name: file.name,
        size: file.size,
        type: file.type,
        url: saveAssetResponse.externalURL!,
        mediaFormat: getFileFormat(file.type),
        isImage: includes(contentType, 'image'),
        isVideo: includes(contentType, 'video'),
    };

    return uploadFileAssetResponse;
};

const cancelCloudUpload = (): void => {
    cancelCloudUploadSource.cancel();
    cancelCloudUploadSource = CancelToken.source();
};

export const cancelUploadFileAsset = (): void => {
    if (isFunction(cancelGetS3Meta)) {
        cancelGetS3Meta();
    }
    if (isFunction(cancelCloudUpload)) {
        cancelCloudUpload();
    }
    if (isFunction(cancelSaveAsset)) {
        cancelSaveAsset();
    }
};

export const getFileFormat = (fileType: string): MediaType => {
    if (fileType) {
        switch (fileType.split('/')[0]) {
            case MediaType.Image:
                return MediaType.Image;

            case MediaType.Video:
                return MediaType.Video;

            case MediaType.Audio:
                return MediaType.Audio;

            case MediaType.Text:
                return MediaType.Text;

            case MediaType.Application:
                return MediaType.Text;

            case MediaType.Raw:
                return MediaType.Text;

            default:
                throw Error(`Invalid file type: ${fileType}`);
        }
    }
    return MediaType.Text;
};
